import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { createLeadSchema } from '#shared';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useForm } from '../../lib/form';
import { useLeadSetup, type CustomField, type LeadDetail, type LeadRow } from '../../lib/leads';
import { Button, Drawer, SelectField, TextAreaField, TextField, cx } from '../ui';
import { FollowupPicker } from './bits';

interface DuplicateInfo { id: string; number: number; name: string; ownerName: string | null; statusName: string; canOpen: boolean }

/** Add or edit a lead. Name or phone is enough; everything else is optional. */
export function LeadForm({ lead, onClose, onSaved, onOpenLead }: {
  lead?: LeadDetail | null;
  onClose: () => void;
  onSaved?: (lead: LeadRow) => void;
  onOpenLead?: (id: string) => void;
}) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const setup = useLeadSetup().data;
  const [duplicate, setDuplicate] = useState<DuplicateInfo | null>(null);
  const [more, setMore] = useState(!!lead && !!(lead.altPhone || lead.address || lead.state));
  const editing = !!lead;

  const form = useForm(createLeadSchema, {
    name: lead?.name ?? '', phone: lead?.phone ?? '', altPhone: lead?.altPhone ?? '', email: lead?.email ?? '',
    company: lead?.company ?? '', city: lead?.city ?? '', state: lead?.state ?? '', address: lead?.address ?? '',
    notes: lead?.notes ?? '', value: lead?.value ?? '', isQualified: lead?.isQualified ?? false,
    statusId: lead?.statusId ?? '', sourceId: lead?.sourceId ?? '', campaignId: lead?.campaignId ?? '', ownerId: lead?.ownerId ?? '',
    tagIds: lead?.tags.map((t) => t.id) ?? [], custom: lead?.custom ?? {},
    nextFollowupAt: null, followupNote: '',
  });
  const v = form.values;
  const custom = (v.custom ?? {}) as Record<string, unknown>;

  const save = (allowDuplicate = false) =>
    form.submit(async (data) => {
      try {
        // People who can't assign never send an owner (the field isn't shown to them).
        const { ownerId, nextFollowupAt, followupNote, ...rest } = data;
        const withOwner = can('leads.assign') ? { ...rest, ownerId } : rest;
        const saved = editing
          ? await api.patch<LeadRow>(`/leads/${lead.id}`, withOwner)
          : await api.post<LeadRow>('/leads', { ...withOwner, nextFollowupAt, followupNote, allowDuplicate });
        toast.success(editing ? 'Lead updated.' : `Lead L-${saved.number} added${saved.ownerName ? ` and assigned to ${saved.ownerName}` : ''}.`);
        void qc.invalidateQueries({ queryKey: ['leads'] });
        void qc.invalidateQueries({ queryKey: ['lead'] });
        void qc.invalidateQueries({ queryKey: ['dashboard'] });
        onSaved?.(saved);
        onClose();
      } catch (err) {
        if (err instanceof ApiError && err.code === 'DUPLICATE_LEAD') {
          setDuplicate((err.data?.lead as DuplicateInfo) ?? null);
          return;
        }
        throw err;
      }
    });

  const toggleTag = (id: string) => {
    const list = (v.tagIds ?? []) as string[];
    form.set('tagIds', list.includes(id) ? list.filter((t) => t !== id) : [...list, id]);
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={editing ? `Edit L-${lead.number}` : 'Add a lead'}
      description={editing ? undefined : 'Just a name or phone number is enough to start.'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => void save()} loading={form.submitting}>{editing ? 'Save changes' : 'Add lead'}</Button>
        </>
      }
    >
      <form className="space-y-5" noValidate onSubmit={(e) => { e.preventDefault(); void save(); }}>
        {duplicate && (
          <div role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
            <p className="flex items-start gap-2 font-medium">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              This contact already exists as L-{duplicate.number}: {duplicate.name} ({duplicate.statusName}
              {duplicate.ownerName ? `, with ${duplicate.ownerName}` : ', not assigned'}).
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {duplicate.canOpen && onOpenLead && (
                <Button size="sm" onClick={() => { onClose(); onOpenLead(duplicate.id); }}>Open L-{duplicate.number}</Button>
              )}
              {!editing && <Button size="sm" variant="secondary" onClick={() => void save(true)}>Add as a new lead anyway</Button>}
            </div>
          </div>
        )}

        <TextField label="Name" autoFocus placeholder="e.g. Ravi Kumar" {...form.bind('name')} />
        <div className="grid gap-5 sm:grid-cols-2">
          <TextField label="Mobile number" type="tel" inputMode="tel" placeholder="98765 43210" {...form.bind('phone')} onBlur={() => setDuplicate(null)} />
          <TextField label="Email" type="email" inputMode="email" {...form.bind('email')} />
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <TextField label="Company" {...form.bind('company')} />
          <TextField label="City" {...form.bind('city')} />
        </div>

        {setup && (
          <div className="grid gap-5 sm:grid-cols-2">
            <SelectField label="Status" {...form.bind('statusId')}>
              {!editing && <option value="">{setup.statuses.find((s) => s.isDefault)?.name ?? 'Default'} (new leads)</option>}
              {setup.statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </SelectField>
            <SelectField label="Source" hint="Where did this lead come from?" {...form.bind('sourceId')}>
              <option value="">— Not set —</option>
              {setup.sources.filter((s) => s.isActive || s.id === v.sourceId).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </SelectField>
            {can('leads.assign') && (
              <SelectField label="Assigned to" {...form.bind('ownerId')} hint={!editing ? 'Leave as automatic to use your auto-assign rules.' : undefined}>
                <option value="">{editing ? '— Nobody —' : 'Automatic'}</option>
                {setup.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </SelectField>
            )}
            {setup.campaigns.length > 0 && (
              <SelectField label="Campaign" {...form.bind('campaignId')}>
                <option value="">— None —</option>
                {setup.campaigns.filter((c) => c.isActive || c.id === v.campaignId).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </SelectField>
            )}
          </div>
        )}

        <TextAreaField label="Requirement / notes" placeholder="What are they looking for? Budget, date, quantity…" {...form.bind('notes')} />

        <div className="grid gap-5 sm:grid-cols-2">
          <TextField label="Expected deal value (₹)" inputMode="numeric" placeholder="e.g. 50000" {...form.bind('value')} />
          <label className="flex items-center gap-3 self-end pb-2">
            <input type="checkbox" className="size-5 rounded accent-brand-600" checked={!!v.isQualified} onChange={(e) => form.set('isQualified', e.target.checked)} />
            <span className="text-sm font-medium">Qualified lead (genuine interest)</span>
          </label>
        </div>

        {setup && setup.tags.length > 0 && (
          <fieldset>
            <legend className="mb-2 text-sm font-medium">Tags</legend>
            <div className="flex flex-wrap gap-2">
              {setup.tags.map((t) => {
                const on = ((v.tagIds ?? []) as string[]).includes(t.id);
                return (
                  <button key={t.id} type="button" onClick={() => toggleTag(t.id)} aria-pressed={on}
                    className={cx('rounded-full px-3 py-1 text-sm ring-1 transition', on ? 'text-white' : 'ring-slate-300 dark:ring-slate-700')}
                    style={on ? { backgroundColor: t.color, boxShadow: `0 0 0 1px ${t.color}` } : undefined}>
                    {t.name}
                  </button>
                );
              })}
            </div>
          </fieldset>
        )}

        {setup?.customFields.map((f) => (
          <CustomInput key={f.id} field={f} value={custom[f.key]} error={form.errors[`custom.${f.key}`]}
            onChange={(val) => form.set('custom', { ...custom, [f.key]: val })} />
        ))}

        {!more ? (
          <button type="button" className="text-sm font-medium text-brand-700 hover:underline" onClick={() => setMore(true)}>
            + Add other phone, state and address
          </button>
        ) : (
          <div className="space-y-5">
            <TextField label="Other phone" type="tel" inputMode="tel" {...form.bind('altPhone')} />
            <TextField label="State" {...form.bind('state')} />
            <TextAreaField label="Address" {...form.bind('address')} />
          </div>
        )}

        {!editing && (
          <fieldset className="rounded-xl bg-slate-50 p-4 dark:bg-slate-800/50">
            <legend className="sr-only">First follow-up</legend>
            <p className="mb-3 text-sm font-medium">When should someone follow up? <span className="font-normal text-slate-500">(optional)</span></p>
            <FollowupPicker
              value={(v.nextFollowupAt as string) ?? ''}
              onChange={(iso) => form.set('nextFollowupAt', iso)}
              note={(v.followupNote as string) ?? ''}
              onNoteChange={(n) => form.set('followupNote', n)}
            />
          </fieldset>
        )}
        <button type="submit" hidden />
      </form>
    </Drawer>
  );
}

export function CustomInput({ field, value, onChange, error }: { field: CustomField; value: unknown; onChange: (v: unknown) => void; error?: string }) {
  const label = field.label + (field.isRequired ? ' *' : '');
  const str = value === null || value === undefined ? '' : String(value);
  switch (field.type) {
    case 'select':
      return (
        <SelectField label={label} value={str} onChange={(e) => onChange(e.target.value)} error={error}>
          <option value="">— Choose —</option>
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </SelectField>
      );
    case 'checkbox':
      return (
        <label className="flex items-center gap-3">
          <input type="checkbox" className="size-5 rounded accent-brand-600" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
          <span className="text-sm font-medium">{field.label}</span>
        </label>
      );
    case 'textarea':
      return <TextAreaField label={label} value={str} onChange={(e) => onChange(e.target.value)} error={error} />;
    default:
      return (
        <TextField
          label={label}
          value={str}
          error={error}
          onChange={(e) => onChange(e.target.value)}
          type={field.type === 'date' ? 'date' : field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : field.type === 'phone' ? 'tel' : 'text'}
          inputMode={field.type === 'number' ? 'decimal' : field.type === 'phone' ? 'tel' : undefined}
        />
      );
  }
}
